(function() {
  'use strict';

  const DEBUG = false;
  const NAMESPACE = 'theme:subscriptions';

  function log(...args) {
    if (DEBUG) {
      console.debug(`[${NAMESPACE}]`, ...args);
    }
  }

  function findVariantInputs(productRoot) {
    const forms = Array.from(productRoot.querySelectorAll('form[action*="/cart/add"]'));
    const variantInputs = [];

    forms.forEach(form => {
      const hiddenInput = form.querySelector('input[name="id"][type="hidden"]');
      if (hiddenInput) variantInputs.push(hiddenInput);

      const selectInput = form.querySelector('select[name="id"]');
      if (selectInput && !variantInputs.includes(selectInput)) {
        variantInputs.push(selectInput);
      }

      const visibleInput = form.querySelector('input[name="id"]:not([type="hidden"])');
      if (visibleInput && !variantInputs.includes(visibleInput)) {
        variantInputs.push(visibleInput);
      }
    });

    return variantInputs;
  }

  function updateVariantInputs(productRoot, variantId) {
    const variantInputs = findVariantInputs(productRoot);
    const updated = [];

    variantInputs.forEach(input => {
      if (input.value !== String(variantId)) {
        if (input.tagName === 'SELECT') {
          input.value = String(variantId);
          const option = input.querySelector(`option[value="${variantId}"]`);
          if (option) option.selected = true;
        } else {
          input.value = String(variantId);
        }

        if (input.tagName === 'INPUT') {
          input.setAttribute('data-variant-sync', Date.now().toString());
        }

        updated.push(input);
      }
    });

    return updated;
  }

  function triggerFormChangeEvents(productRoot) {
    const forms = Array.from(productRoot.querySelectorAll('form[action*="/cart/add"]'));
    forms.forEach(form => {
      form.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
    });
  }

  function forceAppResync(productRoot, variantId) {
    const variantInputs = findVariantInputs(productRoot);
    
    variantInputs.forEach(input => {
      if (input.tagName === 'INPUT') {
        input.setAttribute('data-variant-sync', Date.now().toString());
      }
      input.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
    });
  }

  function syncSubscriptionWidget(event) {
    const { variant, productRootEl } = event.detail;

    if (!variant?.id || !productRootEl) return;

    const variantId = String(variant.id);
    const priceElement = findATCButtonPriceElement(productRootEl);
    if (priceElement?.hasAttribute('data-subscription-price-override')) {
      updatePrice(productRootEl, null);
    }

    updateVariantInputs(productRootEl, variantId);

    requestAnimationFrame(() => {
      triggerFormChangeEvents(productRootEl);
      forceAppResync(productRootEl, variantId);

      setTimeout(() => {
        const currentInputs = findVariantInputs(productRootEl);
        let needsResync = false;

        currentInputs.forEach(input => {
          if (input.value !== variantId) {
            input.value = variantId;
            if (input.tagName === 'SELECT') {
              const option = input.querySelector(`option[value="${variantId}"]`);
              if (option) option.selected = true;
            }
            needsResync = true;
          }
        });

        if (needsResync) {
          triggerFormChangeEvents(productRootEl);
          forceAppResync(productRootEl, variantId);
        }

        const subscriptionPrice = getSellingPlanPrice(productRootEl);
        if (subscriptionPrice) {
          updatePrice(productRootEl, subscriptionPrice);
        }
      }, 150);
    });
  }

  function formatPrice(cents) {
    if (typeof window.formatMoney === 'function') {
      try {
        return window.theme?.moneyFormat 
          ? window.formatMoney(cents, window.theme.moneyFormat)
          : window.formatMoney(cents);
      } catch (e) {
        log('Error formatting price:', e);
      }
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  function getCurrentVariantPrice(productRoot) {
    const alpineData = productRoot._x_dataStack?.[0];
    if (alpineData?.currentVariant?.price) {
      return alpineData.currentVariant.price;
    }
    const variantInput = productRoot.querySelector('input[name="id"], select[name="id"]');
    if (variantInput && alpineData?.product?.variants) {
      const variant = alpineData.product.variants.find(v => String(v.id) === String(variantInput.value));
      if (variant) return variant.price;
    }
    return null;
  }

  function findPriceElements(productRoot) {
    const elements = [];
    const atcPrice = productRoot.querySelector('[data-atc-price]') ||
                     productRoot.querySelector('.add-to-cart-btn span[x-html*="formatMoney(currentPrice)"]');
    if (atcPrice) elements.push(atcPrice);
    
    const mainPrice = productRoot.querySelector('[data-product-price]');
    if (mainPrice) elements.push(mainPrice);
    
    return elements;
  }

  function updateAlpinePrice(productRoot, priceInCents) {
    const alpineData = productRoot._x_dataStack?.[0];
    if (!alpineData) return false;

    if (!alpineData._originalCurrentPriceGetter) {
      const protoDescriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(alpineData), 'currentPrice');
      alpineData._originalCurrentPriceGetter = protoDescriptor?.get?.bind(alpineData) || 
        (() => alpineData.currentVariant?.price || 0);
    }

    if (priceInCents && priceInCents > 0) {
      alpineData.subscriptionPrice = priceInCents;
      Object.defineProperty(alpineData, 'currentPrice', {
        get: function() {
          return this.subscriptionPrice || this._originalCurrentPriceGetter.call(this);
        },
        configurable: true,
        enumerable: true
      });
      
      if (window.Alpine?.nextTick) {
        window.Alpine.nextTick(() => {
          const priceElements = findPriceElements(productRoot);
          const expectedText = formatPrice(priceInCents);
          priceElements.forEach(el => {
            if (el.textContent.trim() !== expectedText.trim()) {
              if (window.Alpine.mutateDom) {
                window.Alpine.mutateDom(() => {
                  el.innerHTML = expectedText;
                });
              } else {
                el.innerHTML = expectedText;
              }
            }
          });
        });
      }
      return true;
    } else {
      delete alpineData.subscriptionPrice;
      if (alpineData._originalCurrentPriceGetter) {
        Object.defineProperty(alpineData, 'currentPrice', {
          get: alpineData._originalCurrentPriceGetter,
          configurable: true,
          enumerable: true
        });
      }
      return true;
    }
  }

  function updatePrice(productRoot, priceInCents) {
    if (updateAlpinePrice(productRoot, priceInCents)) {
      setTimeout(() => {
        const priceElements = findPriceElements(productRoot);
        const expectedText = formatPrice(priceInCents || getCurrentVariantPrice(productRoot) || 0);
        priceElements.forEach(el => {
          if (el.textContent.trim() !== expectedText.trim()) {
            if (window.Alpine?.mutateDom) {
              window.Alpine.mutateDom(() => {
                el.innerHTML = expectedText;
              });
            } else {
              el.innerHTML = expectedText;
            }
          }
        });
      }, 50);
    }
  }

  function getSellingPlanPrice(productRoot) {
    const container = productRoot.querySelector('.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden)');
    if (!container) return null;

    const selectedRadio = container.querySelector('input[type="radio"]:checked');
    if (!selectedRadio) return null;

    const priceAttributes = ['variantPrice', 'variant-price', 'price', 'sellingPlanPrice', 'selling-plan-price'];
    
    for (const attrName of priceAttributes) {
      const priceValue = selectedRadio.dataset[attrName] || selectedRadio.getAttribute(`data-${attrName}`);
      if (priceValue) {
        if (typeof priceValue === 'string') {
          const priceMatch = priceValue.match(/[\d,]+\.?\d*/);
          if (priceMatch) {
            const dollars = parseFloat(priceMatch[0].replace(/,/g, ''));
            if (!isNaN(dollars) && dollars > 0) {
              return Math.round(dollars * 100);
            }
          }
        } else {
          const num = parseInt(priceValue, 10);
          if (!isNaN(num) && num > 0) {
            return num < 1000 ? Math.round(num * 100) : num;
          }
        }
      }
    }

    const parent = selectedRadio.closest('label, div, li');
    if (parent) {
      for (const attrName of priceAttributes) {
        const priceValue = parent.dataset[attrName] || parent.getAttribute(`data-${attrName}`);
        if (priceValue) {
          const priceMatch = priceValue.match(/[\d,]+\.?\d*/);
          if (priceMatch) {
            const dollars = parseFloat(priceMatch[0].replace(/,/g, ''));
            if (!isNaN(dollars) && dollars > 0) {
              return Math.round(dollars * 100);
            }
          }
        }
      }
    }

    return null;
  }

  function setupSubscriptionListeners(productRoot) {
    if (productRoot.hasAttribute('data-subscription-listeners-setup')) return;

    const handler = (event) => {
      if (event.target.type !== 'radio' || 
          !event.target.closest('.shopify_subscriptions_app_block') ||
          !event.target.checked) {
        return;
      }

      const selectedOption = event.target;
      const sellingPlanId = selectedOption.dataset.sellingPlanId || 
                           selectedOption.getAttribute('data-selling-plan-id') ||
                           selectedOption.closest('label, div')?.querySelector('[data-selling-plan-id]')?.getAttribute('data-selling-plan-id');
      
      const isSubscriptionOption = selectedOption.closest('.shopify_subscriptions_app_block') &&
                                   sellingPlanId &&
                                   sellingPlanId !== '' &&
                                   sellingPlanId !== 'null' &&
                                   selectedOption.value !== '' &&
                                   selectedOption.value !== 'one-time';

      const priceAttributes = ['variantPrice', 'variant-price', 'price', 'sellingPlanPrice', 'selling-plan-price'];
      let subscriptionPrice = null;

      for (const attrName of priceAttributes) {
        subscriptionPrice = selectedOption.dataset[attrName] || selectedOption.getAttribute(`data-${attrName}`);
        if (subscriptionPrice) break;
      }

      if (!subscriptionPrice) {
        const parent = selectedOption.closest('label, div, li, span');
        if (parent) {
          for (const attrName of priceAttributes) {
            subscriptionPrice = parent.dataset[attrName] || parent.getAttribute(`data-${attrName}`);
            if (subscriptionPrice) break;
          }
        }
      }

      let priceInCents = null;

      if (subscriptionPrice) {
        if (typeof subscriptionPrice === 'string') {
          const priceMatch = subscriptionPrice.match(/[\d,]+\.?\d*/);
          if (priceMatch) {
            const dollars = parseFloat(priceMatch[0].replace(/,/g, ''));
            if (!isNaN(dollars) && dollars > 0) {
              priceInCents = Math.round(dollars * 100);
            }
          } else {
            const numValue = parseFloat(subscriptionPrice.replace(/[^0-9.]/g, ''));
            if (!isNaN(numValue) && numValue > 0) {
              priceInCents = numValue < 1000 ? Math.round(numValue * 100) : Math.round(numValue);
            }
          }
        } else if (typeof subscriptionPrice === 'number') {
          priceInCents = subscriptionPrice < 1000 ? Math.round(subscriptionPrice * 100) : Math.round(subscriptionPrice);
        }

        if (!priceInCents || !isSubscriptionOption) {
          priceInCents = null;
        }
      }

      if (!priceInCents || !isSubscriptionOption) {
        priceInCents = getCurrentVariantPrice(productRoot);
      }

      const customEvent = new CustomEvent('theme:subscription:option:change', {
        bubbles: true,
        cancelable: true,
        detail: {
          productRootEl: productRoot,
          price: priceInCents,
          purchaseType: isSubscriptionOption ? 'subscription' : 'one-time',
          sellingPlanId: sellingPlanId,
          selectedOption: selectedOption
        }
      });
      
      productRoot.dispatchEvent(customEvent);
      document.dispatchEvent(customEvent);
    };

    productRoot.addEventListener('change', handler, true);
    productRoot.setAttribute('data-subscription-listeners-setup', 'true');
  }

  function setupPriceUpdateListener() {
    document.addEventListener('theme:subscription:option:change', (event) => {
      const { productRootEl, price } = event.detail;
      if (productRootEl && price && price > 0) {
        updatePrice(productRootEl, price);
      }
    });
  }

  function checkInitialSubscriptionPrice(productRoot) {
    const subscriptionPrice = getSellingPlanPrice(productRoot);
    if (subscriptionPrice) {
      updatePrice(productRoot, subscriptionPrice);
    }
  }

  function initSubscriptionListeners() {
    const productRoots = Array.from(
      document.querySelectorAll('[data-product-root], [x-data*="Product"]')
    );

    productRoots.forEach(root => {
      const productRoot = root.closest('[data-product-root]') || root;
      setupSubscriptionListeners(productRoot);
      setTimeout(() => checkInitialSubscriptionPrice(productRoot), 200);
    });

    if (window.MutationObserver) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && node.classList?.contains('shopify_subscriptions_app_block')) {
              const productRoot = node.closest('[data-product-root]') || 
                                node.closest('[x-data*="Product"]');
              if (productRoot) {
                setupSubscriptionListeners(productRoot);
                setTimeout(() => checkInitialSubscriptionPrice(productRoot), 200);
              }
            }
          });
        });
      });

      document.querySelectorAll('[data-product-root], [x-data*="Product"]').forEach(root => {
        observer.observe(root, { childList: true, subtree: true });
      });
    }
  }

  function findATCButtonPriceElement(productRoot) {
    return productRoot.querySelector('[data-atc-price]') ||
           productRoot.querySelector('.add-to-cart-btn span[x-html*="formatMoney(currentPrice)"]');
  }

  function init() {
    document.addEventListener('theme:variant:change', syncSubscriptionWidget);

    document.addEventListener('shapes:product:variantchange', (event) => {
      if (event.detail?.variant) {
        const productRoot = event.target.closest('[data-product-root]') || 
                          event.target.closest('[x-data*="Product"]')?.parentElement;
        
        if (productRoot) {
          syncSubscriptionWidget(new CustomEvent('theme:variant:change', {
            detail: {
              variant: event.detail.variant,
              productRootEl: productRoot,
              formEl: productRoot.querySelector('form[action*="/cart/add"]')
            }
          }));
        }
      }
    });

    initSubscriptionListeners();
    setupPriceUpdateListener();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
